/* =========================================================================
   KTM Groceries — theme JS
   Spec §7.1: no innerHTML with catalogue data, no inline handlers, no eval.
   All behaviour is delegated from data-* attributes so the theme stays
   CSP-compatible (script-src 'self').
   ========================================================================= */
(function () {
  "use strict";

  /* ---------------------------------------------------------- announcer -- */
  const live = document.getElementById("a11y-status");
  function announce(msg) {
    if (live) live.textContent = msg;
  }

  /* -------------------------------------------------------- scroll lock -- */
  /**
   * The cart drawer and the mobile menu can both want the page frozen. They each
   * used to write document.body.style.overflow directly, so closing one released
   * the lock the other still needed. Count owners instead.
   */
  const scrollLocks = new Set();
  function lockScroll(owner, on) {
    if (on) scrollLocks.add(owner);
    else scrollLocks.delete(owner);
    document.body.style.overflow = scrollLocks.size ? "hidden" : "";
  }

  /* -------------------------------------------------------------- toast -- */
  let toastTimer;
  function toast(msg) {
    const el = document.getElementById("toast");
    const label = document.getElementById("toastMsg");
    if (!el || !label) return;
    label.textContent = msg; // textContent, never innerHTML
    el.classList.add("show");
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => el.classList.remove("show"), 2400);
  }

  /* --------------------------------------------------------------- cart -- */
  /**
   * Cart state lives in Shopify, not the browser. Everything here talks to the
   * standard Cart AJAX API (/cart.js, /cart/add.js, /cart/change.js), which the
   * local dev harness also implements — so this code is identical in both
   * environments and needs no swap at integration.
   */
  const config = (() => {
    try {
      return JSON.parse(document.getElementById("ktm-config").textContent);
    } catch {
      return { freeDeliveryThreshold: 49.99 };
    }
  })();
  // Math.round, because the threshold is a decimal: 49.99 * 100 is 4998.999… in
  // floating point, and every comparison and "$X more to go" figure below is in
  // whole cents.
  const FREE_DELIVERY = Math.round(Number(config.freeDeliveryThreshold || 49.99) * 100);

  const money = (c) => "$" + (Number(c || 0) / 100).toFixed(2);

  let cart = { item_count: 0, total_price: 0, items: [] };

  async function cartApi(path, body) {
    const res = await fetch(path, {
      method: body ? "POST" : "GET",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: body ? JSON.stringify(body) : undefined,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.description || data.message || "Cart error");
    return data;
  }

  async function refresh() {
    cart = await cartApi("/cart.js");
    renderCart();
  }

  const drawer = document.querySelector("[data-cart-drawer]");
  const cartScrim = document.querySelector("[data-cart-scrim]");
  const listEl = document.querySelector("[data-cart-items]");
  const emptyEl = document.querySelector("[data-cart-empty]");
  const footEl = document.querySelector("[data-cart-foot]");

  function setCount(n) {
    document.querySelectorAll("[data-cart-count]").forEach((el) => {
      el.textContent = String(n);
      el.hidden = n === 0;
    });
    document.querySelectorAll("[data-cart-count-text]").forEach((el) => {
      el.textContent = String(n);
    });
  }

  function lineItem(item) {
    const li = document.createElement("li");
    li.className = "cart-item";

    const src = item.image || (item.featured_image && item.featured_image.url);
    if (src) {
      const img = document.createElement("img");
      img.src = src;
      img.alt = "";
      img.width = 64;
      img.height = 64;
      img.loading = "lazy";
      li.appendChild(img);
    }

    const info = document.createElement("div");
    info.className = "ci-info";

    const title = document.createElement("a");
    title.href = item.url || "#";
    title.className = "ci-title";
    title.textContent = item.product_title || item.title; // untrusted catalogue data
    info.appendChild(title);

    const price = document.createElement("span");
    price.className = "ci-price";
    price.textContent = money(item.final_price != null ? item.final_price : item.price);
    info.appendChild(price);

    const controls = document.createElement("div");
    controls.className = "ci-controls";

    const step = (label, delta, aria) => {
      const b = document.createElement("button");
      b.type = "button";
      // Classed, not styled by element: .ci-controls also holds the Remove button,
      // and a bare `button` rule turned that into a 30px stepper box too.
      b.className = "ci-step";
      b.textContent = label;
      b.setAttribute("aria-label", aria);
      b.addEventListener("click", () => changeQty(item.id, item.quantity + delta, item));
      return b;
    };
    const qty = document.createElement("span");
    qty.className = "ci-qty";
    qty.textContent = String(item.quantity);

    controls.append(
      step("\u2212", -1, "Decrease quantity of " + (item.product_title || item.title)),
      qty,
      step("+", 1, "Increase quantity of " + (item.product_title || item.title))
    );

    const remove = document.createElement("button");
    remove.type = "button";
    remove.className = "ci-remove";
    remove.textContent = "Remove";
    remove.setAttribute("aria-label", "Remove " + (item.product_title || item.title) + " from cart");
    remove.addEventListener("click", () => changeQty(item.id, 0, item));
    controls.appendChild(remove);

    info.appendChild(controls);
    li.appendChild(info);

    const lineTotal = document.createElement("b");
    lineTotal.className = "ci-total";
    lineTotal.textContent = money(
      item.final_line_price != null ? item.final_line_price : item.line_price
    );
    li.appendChild(lineTotal);

    return li;
  }

  async function changeQty(id, quantity, item) {
    try {
      cart = await cartApi("/cart/change.js", { id: String(id), quantity });
      renderCart();
      if (quantity === 0 && item) {
        announce((item.product_title || item.title) + " removed from cart.");
      }
    } catch (err) {
      toast(err.message);
    }
  }

  /**
   * Product cards show a quantity stepper instead of "Add to cart" once the item
   * is in the cart, so repeat-buying never requires opening the product page.
   * Driven off real cart state, so it survives reloads and stays correct after
   * edits made in the drawer.
   */
  function syncCards() {
    const inCart = new Map();
    for (const item of cart.items || []) inCart.set(String(item.id), item.quantity);

    document.querySelectorAll("[data-card-actions]").forEach((slot) => {
      const id = slot.getAttribute("data-variant-id");
      const addBtn = slot.querySelector("[data-add-to-cart]");
      const stepper = slot.querySelector("[data-card-qty]");
      if (!addBtn || !stepper) return;

      const qty = inCart.get(String(id)) || 0;
      const soldOut = addBtn.hasAttribute("disabled") && qty === 0;

      if (qty > 0 && !soldOut) {
        addBtn.hidden = true;
        stepper.hidden = false;
        stepper.querySelector("[data-card-qty-value]").textContent = String(qty);
      } else {
        addBtn.hidden = false;
        stepper.hidden = true;
      }
    });
  }

  document.addEventListener("click", (e) => {
    const btn = e.target.closest("[data-card-step]");
    if (!btn) return;
    e.preventDefault();
    const slot = btn.closest("[data-card-actions]");
    if (!slot) return;
    const id = slot.getAttribute("data-variant-id");
    const current = parseInt(
      slot.querySelector("[data-card-qty-value]").textContent, 10
    ) || 0;
    const next = current + parseInt(btn.getAttribute("data-card-step"), 10);
    changeQty(id, Math.max(0, next));
  });

  function renderCart() {
    setCount(cart.item_count || 0);
    syncCards();
    if (!listEl) return;

    listEl.replaceChildren(...(cart.items || []).map(lineItem));

    const isEmpty = !cart.items || cart.items.length === 0;
    if (emptyEl) emptyEl.hidden = !isEmpty;
    if (footEl) footEl.hidden = isEmpty;
    listEl.hidden = isEmpty;

    const sub = cart.total_price || 0;
    const subEl = document.querySelector("[data-cart-subtotal]");
    if (subEl) subEl.textContent = money(sub);

    const bar = document.querySelector("[data-cart-progress-bar]");
    const txt = document.querySelector("[data-cart-progress-text]");
    const wrap = document.querySelector("[data-cart-progress]");
    if (wrap) wrap.hidden = isEmpty;
    if (bar && txt) {
      const pct = Math.min(100, Math.round((sub / FREE_DELIVERY) * 100));
      bar.style.width = pct + "%";
      if (sub >= FREE_DELIVERY) {
        txt.textContent = "\uD83C\uDF89 You've unlocked free delivery!";
        txt.className = "won";
      } else {
        txt.textContent = money(FREE_DELIVERY - sub) + " more for free delivery";
        txt.className = "";
      }
    }
  }

  function openCart(open) {
    if (!drawer || !cartScrim) return;
    drawer.hidden = !open;
    cartScrim.hidden = !open;
    lockScroll("cart", open);
    if (open) {
      const close = drawer.querySelector("[data-cart-close]");
      if (close) close.focus();
    }
  }

  document.addEventListener("click", (e) => {
    if (e.target.closest("[data-cart-close]") || e.target.closest("[data-cart-scrim]")) {
      openCart(false);
      return;
    }
    const opener = e.target.closest("[data-cart-open]");
    if (opener) {
      e.preventDefault();
      openCart(true);
    }
  });

  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && drawer && !drawer.hidden) openCart(false);
  });

  document.addEventListener("click", async (e) => {
    const btn = e.target.closest("[data-add-to-cart]");
    if (!btn || btn.disabled) return;
    e.preventDefault();

    const id = btn.getAttribute("data-variant-id");
    const title = btn.getAttribute("data-product-title") || "Item";
    // Only the PDP's own button reads the quantity stepper. Scoping this to the
    // enclosing block stops a related-product card on the PDP from inheriting it.
    const scope = btn.closest("[data-pdp-actions]");
    const qtyInput = scope && scope.querySelector("[data-qty-input]");
    const quantity = qtyInput ? Math.max(1, parseInt(qtyInput.value, 10) || 1) : 1;

    btn.disabled = true;
    try {
      await cartApi("/cart/add.js", { id, quantity });
      await refresh();
      toast("Added: " + title);
      announce(quantity + " \u00d7 " + title + " added to cart. Cart has " +
               (cart.item_count || 0) + " items.");
      openCart(true);
    } catch (err) {
      toast(err.message || "Could not add to cart");
    } finally {
      btn.disabled = false;
    }
  });

  /* --- promo code ---------------------------------------------------------- */
  document.addEventListener("submit", (e) => {
    const form = e.target.closest("[data-promo-form]");
    if (!form) return;
    e.preventDefault();
    const note = document.querySelector("[data-promo-note]");
    const code = (form.querySelector("input").value || "").trim().toUpperCase();
    if (!note) return;
    note.hidden = false;
    // Shopify validates and applies discount codes at checkout, so the drawer
    // can only acknowledge the code — it must not display a fake discount.
    note.textContent = code
      ? "Code " + code + " will be applied at checkout."
      : "Enter a discount code.";
  });


  /* ----------------------------------------------------------- wishlist -- */
  /*
   * This used to toggle an emoji, announce it, and store nothing — every heart
   * reset on the next page load, and the header linked to /wishlist, a route that
   * does not exist on this store. Saved items now persist in localStorage and
   * open in a drawer. See snippets/wishlist-drawer.liquid for why not the
   * customer account.
   */
  const WISH_KEY = "ktm:wishlist";

  const wishRead = () => {
    try {
      const raw = JSON.parse(localStorage.getItem(WISH_KEY) || "[]");
      return Array.isArray(raw) ? raw.filter((i) => i && i.url && i.title) : [];
    } catch {
      return []; // corrupt or unavailable (private mode) — behave as empty
    }
  };
  const wishWrite = (items) => {
    try { localStorage.setItem(WISH_KEY, JSON.stringify(items.slice(0, 100))); } catch { /* quota / private mode */ }
  };

  const wishDrawer = document.querySelector("[data-wish-drawer]");
  const wishScrim = document.querySelector("[data-wish-scrim]");
  const wishItemsEl = document.querySelector("[data-wish-items]");
  const wishEmptyEl = document.querySelector("[data-wish-empty]");
  let wishReturnFocus = null;

  function wishPaintCount() {
    const n = wishRead().length;
    document.querySelectorAll("[data-wish-count]").forEach((el) => {
      el.textContent = String(n);
      el.hidden = n === 0;
    });
    document.querySelectorAll("[data-wish-count-text]").forEach((el) => { el.textContent = String(n); });
  }

  // Hearts reflect what is actually saved, on every page.
  function wishPaintButtons() {
    const saved = wishRead();
    document.querySelectorAll("[data-wishlist]").forEach((btn) => {
      const url = btn.getAttribute("data-product-url");
      const on = saved.some((i) => i.url === url);
      btn.setAttribute("aria-pressed", String(on));
      btn.textContent = on ? "❤️" : "\u{1F90D}";
    });
  }

  function wishRender() {
    if (!wishItemsEl) return;
    const items = wishRead();
    wishItemsEl.replaceChildren();
    if (wishEmptyEl) wishEmptyEl.hidden = items.length > 0;

    items.forEach((item) => {
      const li = document.createElement("li");
      // `cart-item`, not `ci` — the drawer row layout hangs off .cart-item and
      // .cart-item img. Guessing `ci` from the .ci-info/.ci-title child classes
      // left every row as an unstyled list-item, image stacked above the text.
      li.className = "cart-item";

      if (item.image) {
        const img = document.createElement("img");
        img.src = item.image;
        img.alt = "";
        img.width = 60;
        img.height = 60;
        img.loading = "lazy";
        li.appendChild(img);
      }

      const info = document.createElement("div");
      info.className = "ci-info";
      const a = document.createElement("a");
      a.className = "ci-title";
      a.href = item.url;
      a.textContent = item.title; // untrusted catalogue string
      info.appendChild(a);
      if (item.price != null && item.price !== "") {
        const p = document.createElement("span");
        p.className = "ci-price";
        p.textContent = money(Number(item.price));
        info.appendChild(p);
      }
      li.appendChild(info);

      const remove = document.createElement("button");
      remove.type = "button";
      remove.className = "ci-remove";
      remove.textContent = "Remove";
      remove.setAttribute("aria-label", "Remove " + item.title + " from saved items");
      remove.addEventListener("click", () => {
        wishWrite(wishRead().filter((i) => i.url !== item.url));
        wishPaintCount();
        wishPaintButtons();
        wishRender();
        announce("Removed from saved items: " + item.title);
      });
      li.appendChild(remove);

      wishItemsEl.appendChild(li);
    });
  }

  function wishOpen(on) {
    if (!wishDrawer || !wishScrim) return;
    if (on) {
      wishReturnFocus = document.activeElement;
      wishRender();
      wishPaintCount();
    }
    wishDrawer.hidden = !on;
    wishScrim.hidden = !on;
    lockScroll("wishlist", on);
    if (on) {
      const close = wishDrawer.querySelector("[data-wish-close]");
      if (close) close.focus();
    } else if (wishReturnFocus && wishReturnFocus.focus) {
      wishReturnFocus.focus();
    }
  }

  document.addEventListener("click", (e) => {
    const open = e.target.closest("[data-wish-open]");
    if (open) {
      // No drawer (JS-less fallback markup missing) — let the href do its job.
      if (!wishDrawer) return;
      e.preventDefault();
      return wishOpen(true);
    }
    if (e.target.closest("[data-wish-close]") || e.target.closest("[data-wish-scrim]")) {
      return wishOpen(false);
    }

    const btn = e.target.closest("[data-wishlist]");
    if (!btn) return;
    e.preventDefault();

    const url = btn.getAttribute("data-product-url");
    const title = btn.getAttribute("data-product-title") || "item";
    if (!url) return; // nothing to link back to; saving it would be a dead entry

    const saved = wishRead();
    const already = saved.some((i) => i.url === url);
    const next = already
      ? saved.filter((i) => i.url !== url)
      : saved.concat([{
          url,
          title,
          price: btn.getAttribute("data-product-price") || "",
          image: btn.getAttribute("data-product-image") || "",
        }]);

    wishWrite(next);
    btn.setAttribute("aria-pressed", String(!already));
    btn.textContent = already ? "\u{1F90D}" : "❤️";
    wishPaintCount();
    if (wishDrawer && !wishDrawer.hidden) wishRender();
    announce((already ? "Removed from" : "Saved to") + " saved items: " + title);
    toast((already ? "Removed from" : "Saved to") + " your list");
  });

  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && wishDrawer && !wishDrawer.hidden) wishOpen(false);
  });

  wishPaintCount();
  wishPaintButtons();

  /* ---------------------------------------------------------- mega menu -- */
  const navItems = Array.from(document.querySelectorAll("[data-nav-item]"));
  const isDesktop = () => window.matchMedia("(min-width: 861px)").matches;

  /**
   * Keeps a dropdown inside the viewport.
   *
   * The panels are position:fixed, because the row they hang off is a horizontal
   * scroll container and would otherwise clip them. Fixed means viewport
   * coordinates, so both axes are set here from the item's measured rect.
   */
  function positionMega(li) {
    const mega = li.querySelector(".mega");
    if (!mega || !isDesktop()) return;

    const GUTTER = 12;
    const viewport = document.documentElement.clientWidth;
    const rect = li.getBoundingClientRect();
    const width = mega.offsetWidth;

    let left = rect.left;
    if (left + width > viewport - GUTTER) left = viewport - GUTTER - width;
    if (left < GUTTER) left = GUTTER;

    /*
     * Anchored to the bar, not to the item, and deliberately not rounded. The
     * bar's bottom is fractional (221.6px); rounding it opened a sub-pixel seam
     * between the item and the panel, which is a place the pointer can fall
     * through and close the menu it is travelling towards.
     */
    const bar = li.closest("nav.main");
    const bottom = bar ? bar.getBoundingClientRect().bottom : rect.bottom;

    mega.style.left = Math.round(left) + "px";
    mega.style.top = bottom + "px";
  }

  function setMega(li, open) {
    const toggle = li.querySelector("[data-mega-toggle]");
    // Placed before it is revealed, so the panel is never painted at 0,0 first.
    // The panel keeps its layout while hidden, so it is measurable here.
    if (open) positionMega(li);
    li.classList.toggle("open", open);
    if (toggle) toggle.setAttribute("aria-expanded", String(open));
    /*
     * The inline coordinates are NOT cleared on close. Clearing them dropped the
     * panel back to the stylesheet's top:0/left:0 while it was still fading out,
     * so every unhover threw the panel into the top-left corner, over the header.
     * They are harmless while hidden and always rewritten before the next open.
     */
  }

  function closeAll(except) {
    navItems.forEach((li) => {
      if (li !== except) setMega(li, false);
    });
  }

  /*
   * A resize or a page scroll moves the bar, and the panels are pinned to viewport
   * coordinates, so they have to follow. Reposition rather than close: the sticky
   * header keeps the bar on screen, and yanking the menu away because the wheel
   * moved a notch is the jitter this is meant to avoid.
   */
  let repositionFrame = 0;
  function repositionOpen() {
    repositionFrame = 0;
    navItems.forEach((li) => {
      if (li.classList.contains("open")) positionMega(li);
    });
  }
  function scheduleReposition() {
    if (!repositionFrame) repositionFrame = requestAnimationFrame(repositionOpen);
  }
  window.addEventListener("resize", scheduleReposition);
  window.addEventListener("scroll", scheduleReposition, { passive: true });

  /*
   * Hover intent. Without a grace period, any pointer path that clips a corner of
   * the panel or passes between two departments closes the menu mid-travel, which
   * reads as flicker. The delay is cancelled the moment the pointer lands back on
   * any part of the menu.
   */
  const CLOSE_DELAY = 140;
  let closeTimer = 0;
  function cancelPendingClose() {
    if (closeTimer) {
      clearTimeout(closeTimer);
      closeTimer = 0;
    }
  }

  navItems.forEach((li) => {
    const toggle = li.querySelector("[data-mega-toggle]");
    if (!toggle) return;

    li.addEventListener("mouseenter", () => {
      if (!isDesktop()) return;
      cancelPendingClose();
      closeAll(li);
      setMega(li, true);
    });
    li.addEventListener("mouseleave", () => {
      if (!isDesktop()) return;
      cancelPendingClose();
      closeTimer = setTimeout(() => {
        closeTimer = 0;
        setMega(li, false);
      }, CLOSE_DELAY);
    });

    toggle.addEventListener("click", (e) => {
      e.preventDefault();
      cancelPendingClose();
      const open = li.classList.contains("open");
      closeAll(li);
      setMega(li, !open);
    });

    // Keyboard: Escape closes and returns focus to the toggle.
    li.addEventListener("keydown", (e) => {
      if (e.key !== "Escape") return;
      if (!li.classList.contains("open")) return;
      cancelPendingClose();
      setMega(li, false);
      toggle.focus();
    });
  });

  document.addEventListener("click", (e) => {
    if (!e.target.closest("[data-nav-item]")) {
      cancelPendingClose();
      closeAll(null);
    }
  });

  /* ------------------------------------------------ department row scroll -- */
  /*
   * 14 departments never fit one row inside .wrap, and wrapping left a second row
   * a third full at every width. The row scrolls instead; the arrows are
   * enhancement only, since it is a native scroller that already works on touch
   * and, being focusable, with a keyboard.
   */
  const navrow = document.querySelector("[data-navrow]");
  const navPrev = document.querySelector("[data-nav-prev]");
  const navNext = document.querySelector("[data-nav-next]");

  if (navrow && navPrev && navNext) {
    const updateArrows = () => {
      const max = navrow.scrollWidth - navrow.clientWidth;
      const scrollable = isDesktop() && max > 4;
      navPrev.hidden = !scrollable || navrow.scrollLeft <= 4;
      navNext.hidden = !scrollable || navrow.scrollLeft >= max - 4;
    };

    const pageBy = (dir) => navrow.scrollBy({ left: dir * navrow.clientWidth * 0.7 });

    navPrev.addEventListener("click", () => pageBy(-1));
    navNext.addEventListener("click", () => pageBy(1));

    // Scrolling the row moves a department out from under its own panel, so this
    // one does close rather than follow.
    navrow.addEventListener("scroll", () => {
      updateArrows();
      cancelPendingClose();
      closeAll(null);
    }, { passive: true });

    window.addEventListener("resize", updateArrows);
    updateArrows();
    window.addEventListener("load", updateArrows);
  }

  /* -------------------------------------------------------- mobile menu -- */
  const nav = document.getElementById("nav");
  const scrim = document.getElementById("scrim");
  const hamburger = document.getElementById("hamb");
  const menuClose = document.getElementById("menu-close");

  const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),[tabindex]:not([tabindex="-1"])';
  const menuOpen = () => !!nav && nav.classList.contains("show");

  function setMenu(open) {
    if (!nav || !scrim || !hamburger) return;
    if (open === menuOpen()) return;

    // Read before the class flip: hiding the panel blurs whatever was focused.
    const focusWasInside = nav.contains(document.activeElement);

    nav.classList.toggle("show", open);
    scrim.classList.toggle("show", open);
    hamburger.setAttribute("aria-expanded", String(open));
    lockScroll("menu", open);

    if (open) {
      if (menuClose) menuClose.focus();
    } else {
      closeAll(null); // collapse any department accordion left open
      // Only reclaim focus if it was ours to reclaim — a breakpoint change or a
      // bfcache restore closes the menu without the user asking.
      if (focusWasInside) hamburger.focus();
    }
  }

  if (hamburger) hamburger.addEventListener("click", () => setMenu(!menuOpen()));
  if (menuClose) menuClose.addEventListener("click", () => setMenu(false));
  if (scrim) scrim.addEventListener("click", () => setMenu(false));

  // Following a link should not leave a locked body behind if navigation stalls.
  if (nav) {
    nav.addEventListener("click", (e) => {
      if (menuOpen() && e.target.closest("a[href]")) setMenu(false);
    });
  }

  document.addEventListener("keydown", (e) => {
    if (!menuOpen()) return;
    if (e.key === "Escape") return setMenu(false);
    if (e.key !== "Tab") return;

    // Trap Tab inside the drawer — the page behind it is inert to the eye but was
    // still fully tabbable.
    const items = [...nav.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (!nav.contains(document.activeElement)) {
      e.preventDefault();
      first.focus();
    } else if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  });

  /*
   * Rotating to landscape or widening past the breakpoint used to strand the page:
   * the drawer's CSS stops applying, but .show and the scroll lock stayed on, so
   * the desktop layout came back unscrollable.
   */
  const mobileMQ = window.matchMedia("(max-width: 860px)");
  const onBreakpoint = (e) => {
    if (!e.matches) setMenu(false);
  };
  if (mobileMQ.addEventListener) mobileMQ.addEventListener("change", onBreakpoint);
  else if (mobileMQ.addListener) mobileMQ.addListener(onBreakpoint);

  // Back-navigation on iOS restores the page from bfcache exactly as it was left,
  // menu open and body locked included.
  window.addEventListener("pageshow", (e) => {
    if (e.persisted) setMenu(false);
  });

  /* ------------------------------------------- delivery / collect tabs --- */
  // Single store, so Click & Collect is one fixed address — the postcode form is
  // hidden on that tab and the store address shown instead.
  // The delivery bullets are read from the markup rather than repeated here.
  // Hard-coding them meant this list had drifted out of sync with hero.liquid and
  // still carried two claims the DELIVERY CLAIMS suite bans on rendered pages \u2014
  // "Australia-wide shipping fallback" and a delivery-slot promise. The tests
  // never saw them because they only ever existed inside this script.
  const methodListEl = document.getElementById("methodList");
  const deliveryItems = methodListEl
    ? Array.from(methodListEl.querySelectorAll("li")).map((li) => li.textContent.replace(/^\s*\u2713\s*/, "").trim())
    : [];

  const COPY = {
    delivery: {
      title: "\u{1F4CD} Check if we deliver to you",
      button: "Check",
      showForm: true,
      items: deliveryItems,
    },
    collect: {
      title: "\u{1F3EC} Collect from our Tarneit grocery store",
      button: "Check",
      showForm: false,
      // The hours and the call-ahead are the two things a collecting customer
      // needs. Both read from settings so they cannot drift from the pickup
      // panel or the slot picker.
      items: [
        "Order online, then collect \u2014 no delivery fee",
        config.storeHours
          ? "Collection " + config.storeHours
          : "Collection during our published hours",
        config.pickupCallAhead
          ? config.pickupCallAhead + (config.phone ? " " + config.phone : "")
          : "Please call before coming to collect",
        "We'll inform you the moment it's ready",
      ],
    },
  };

  const tabs = Array.from(document.querySelectorAll("[data-method-tab]"));
  tabs.forEach((tab) => {
    tab.addEventListener("click", () => {
      const key = tab.getAttribute("data-method-tab");
      const copy = COPY[key];
      if (!copy) return;

      tabs.forEach((t) => t.setAttribute("aria-selected", String(t === tab)));

      const title = document.getElementById("methodTitle");
      const button = document.getElementById("methodBtn");
      const list = document.getElementById("methodList");
      const form = document.getElementById("methodForm");
      const addr = document.getElementById("pickupAddr");
      if (title) title.textContent = copy.title;
      if (button) button.textContent = copy.button;
      if (form) form.hidden = !copy.showForm;
      if (addr) addr.hidden = copy.showForm;

      // Rebuilt with DOM construction, not innerHTML.
      if (list) {
        list.replaceChildren();
        copy.items.forEach((text) => {
          const li = document.createElement("li");
          const tick = document.createElement("span");
          tick.className = "ck";
          tick.setAttribute("aria-hidden", "true");
          tick.textContent = "✓";
          li.append(tick, document.createTextNode(text));
          list.appendChild(li);
        });
      }
      // The result of a previous check belongs to the delivery tab only.
      const res = document.querySelector("[data-postcode-result]");
      if (res) res.hidden = true;

      announce(key === "delivery" ? "Delivery selected" : "Click and Collect selected");
    });
  });

  /* --------------------------------------------------- back to top ------- */
  // The anchor already works on its own; this only upgrades the jump to a smooth
  // scroll, and skips even that when the visitor asks for reduced motion.
  const backToTop = document.querySelector("[data-back-to-top]");
  if (backToTop) {
    backToTop.addEventListener("click", (e) => {
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: "smooth" });
      // Keep the keyboard where the link pointed, which preventDefault would lose.
      const top = document.getElementById("top");
      if (top) top.focus({ preventScroll: true });
    });
  }

  /* -------------------------------------------------- delivery checker ---- */
  /*
   * "Check if we deliver to you", by distance rather than by postcode.
   *
   * It used to hold a list of postcodes and answer yes to anything on it. A
   * postcode is not a circle round the store: 3030 stretches from Werribee at
   * 6.3km to Cocoroc at 13.9km, so one answer covered both the cheapest delivery
   * zone and a suburb outside the delivery area altogether — and every 3030
   * customer was told free delivery applied.
   *
   * Now it measures. snippets/delivery-suburbs.liquid carries per-suburb
   * coordinates (generated by dev/build-delivery-suburbs.mjs), the store's
   * position comes from theme settings, and the distance decides the zone.
   *
   * Straight-line, not driving: Shopify's local delivery zones measure a
   * straight-line radius from the location, so measuring the same way is what
   * keeps this box and the checkout in agreement. Changing it to road distance
   * would make the two disagree at every zone boundary.
   */
  const pcForm = document.querySelector("[data-postcode-form]");
  const pcResult = document.querySelector("[data-postcode-result]");
  if (pcForm && pcResult) {
    const threshold = pcForm.getAttribute("data-threshold") || "";
    const suburb = pcForm.getAttribute("data-suburb") || "us";
    // Only used in the "we don't have that on our list" answer, which now offers
    // a phone number instead of a flat refusal.
    const phone = pcForm.getAttribute("data-phone") || "";
    const storeLat = Number(pcForm.getAttribute("data-store-lat"));
    const storeLng = Number(pcForm.getAttribute("data-store-lng"));

    /*
     * Zones as "km: minimum order", one per line, mirroring Settings → Shipping
     * and delivery → Local delivery. Sorted by distance so the nearest zone wins
     * regardless of the order they were typed in, and anything unparseable is
     * dropped rather than treated as a zone at 0km that swallows every customer.
     */
    const bands = (pcForm.getAttribute("data-bands") || "")
      .split(/[\n;]+/)
      .map((line) => {
        const m = line.match(/(-?[\d.]+)\s*(?:km)?\s*[:=]\s*\$?\s*([\d.]+)/i);
        if (!m) return null;
        const km = Number(m[1]);
        const min = Number(m[2]);
        return km > 0 && min >= 0 ? { km, min } : null;
      })
      .filter(Boolean)
      .sort((a, b) => a.km - b.km);

    let suburbs = [];
    try {
      const raw = document.getElementById("ktm-suburbs");
      if (raw) suburbs = JSON.parse(raw.textContent) || [];
    } catch {
      suburbs = []; // falls through to the "type your suburb" path below
    }

    const R = 6371.0088; // mean Earth radius, km
    const rad = (d) => (d * Math.PI) / 180;
    const distanceKm = (lat, lng) => {
      const dLat = rad(lat - storeLat);
      const dLng = rad(lng - storeLng);
      const h =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(rad(storeLat)) * Math.cos(rad(lat)) * Math.sin(dLng / 2) ** 2;
      return 2 * R * Math.asin(Math.sqrt(h));
    };

    // [name, postcode, lat, lng] -> { name, postcode, km }, nearest first.
    const measured = suburbs
      .map(([name, postcode, lat, lng]) => ({
        name,
        postcode,
        km: distanceKm(lat, lng),
      }))
      .sort((a, b) => a.km - b.km);

    // Title Case for display: the table is uppercase, and "WERRIBEE SOUTH"
    // shouted back at a customer reads like an error message. Acronyms are the
    // exception — Australia Post lists the bases as LAVERTON RAAF and WILLIAMS
    // RAAF, and "Laverton Raaf" reads like a typo.
    const KEEP_CAPS = new Set(["RAAF"]);
    const pretty = (s) =>
      s
        .split(" ")
        .map((w) =>
          KEEP_CAPS.has(w)
            ? w
            : w.toLowerCase().replace(/(^|-)([a-z])/g, (_, sep, c) => sep + c.toUpperCase()))
        .join(" ");

    const zoneFor = (km) => bands.find((b) => km <= b.km) || null;
    const round = (km) => (km < 15 ? km.toFixed(1) : String(Math.round(km)));
    // The last band is the furthest FREE-delivery zone, not the edge of the
    // delivery area: past it is a charge, not a refusal. Nothing quotes it at the
    // customer as a limit any more, so it is only read through zoneFor/
    // nearBoundary below.

    /*
     * A suburb's coordinate is its centroid, and a suburb is not a point: around
     * here they run 1-3km across, so an address can sit a kilometre or more
     * either side of the number we hold. Altona Meadows measures 12.01km against
     * a 12km zone — ten metres outside, on a figure that cannot be that precise.
     *
     * Two different problems, treated differently:
     *
     *   The outer limit decides deliver / don't deliver, so a suburb just past it
     *   is answered as a maybe and checkout settles it. Refusing a suburb whose
     *   near half is inside the area turns away business over rounding.
     *
     *   The inner boundaries only decide which minimum order applies, and
     *   checkout prices that anyway. Hedging those swallowed the whole range —
     *   the zones are 2km apart, so a 1.5km tolerance either side of each left
     *   just seven suburbs with a straight answer. They get the zone plus a note
     *   that the exact figure is settled at checkout.
     */
    const EDGE_KM = 1.5;
    const nearBoundary = (km) => bands.some((b) => Math.abs(km - b.km) <= EDGE_KM);

    // One list for the datalist, so the autocomplete offers exactly the suburbs
    // the checker can answer for — no suggestion that leads to "we don't know
    // that one".
    const datalist = document.getElementById("ktm-suburb-list");
    if (datalist && measured.length) {
      const frag = document.createDocumentFragment();
      for (const s of measured) {
        const opt = document.createElement("option");
        opt.value = pretty(s.name); // textContent, never innerHTML
        frag.appendChild(opt);
      }
      datalist.appendChild(frag);
    }

    const say = (state, text) => {
      pcResult.className = "postcode-result " + state;
      pcResult.textContent = text;
      pcResult.hidden = false;
      announce(text);
    };

    /*
     * The genuine no: somewhere the coordinate table has never heard of.
     *
     * It is deliberately not phrased as "outside our Nkm delivery area" any more.
     * Since delivery past the free zones is a matter of price rather than
     * possibility, quoting the free-delivery radius as if it were the edge of the
     * world would turn away places the store might well serve. This says what is
     * actually true — we do not have that place on our list — and invites contact.
     */
    /*
     * One sentence, three call sites, so the collection claim can never say two
     * different things in two replies.
     *
     * Three states, and they are genuinely different:
     *   bookable  — a collection time can be chosen online (PickEasy offers pickup)
     *   by phone  — collection happens, arranged on a call, no slot to book
     *   neither   — say nothing
     */
    const collectLine = () => {
      if (config.clickCollectEnabled) {
        return " Click & Collect from our " + suburb + " store is always free.";
      }
      if (config.collectionByPhone) {
        return " You can also collect from our " + suburb + " warehouse — give us " +
               "a call on " + phone + " and we'll arrange a time.";
      }
      return "";
    };

    const tooFarFor = (what) =>
      "We don't have " + what + " on our delivery list — it may be too far, or we " +
      "may just not have it listed yet. Give us a call on " + phone + " and we'll " +
      "tell you straight away." +
      /*
       * Collection is offered here, and the wording is load-bearing.
       *
       * This is the reply shown to people we probably cannot deliver to, so it is
       * the most valuable place on the site to mention collection — and the most
       * damaging place to overstate it. "Click & Collect" implies a slot they can
       * book, which PickEasy does not offer. Collection is real but arranged on
       * the phone, and the sentence already hands them the number.
       */
      collectLine();

    /*
     * Past the last zone is a price, not a refusal.
     *
     * Client, 1 Aug 2026: "More than 12 kms extra charge may apply." So the bands
     * describe where delivery is FREE above a minimum order, and beyond them the
     * van still goes — it just costs. This used to answer a flat "we're not
     * delivering to Caroline Springs yet", which turned away a suburb the store
     * actually serves.
     *
     * It stops short of promising the delivery itself, because the theme cannot
     * know how far Shopify's own zones reach: it says a charge applies and sends
     * them to checkout, which prices the real street address and is the only
     * thing that can say yes for certain.
     */
    const answerFor = (entry) => {
      const zone = zoneFor(entry.km);
      const where = pretty(entry.name);
      const km = round(entry.km);

      /*
       * A suburb's coordinate is its centroid, and suburbs here run 1-3km across,
       * so an address can sit a kilometre either side of the number we hold —
       * Altona Meadows measures 12.01km against a 12km zone. Near a boundary the
       * answer names the likely zone and lets checkout settle it, rather than
       * quoting a threshold to the half of the suburb it does not apply to.
       */
      const borderline = nearBoundary(entry.km);

      if (zone) {
        say(
          "yes",
          "\u{1F389} Yes — we deliver to " + where + ", about " + km +
            "km from our " + suburb + " store. Free delivery on orders over $" +
            zone.min.toFixed(2) +
            (borderline
              ? " for most of " + where + " — your exact address sets the final " +
                "figure at checkout." + collectLine()
              : (config.clickCollectEnabled
                  ? ", or collect from the store for free."
                  : "." + collectLine()))
        );
        return;
      }

      /*
       * "Confirm we can reach you", not just "show the cost". The coordinate
       * table runs to 22km and the bands stop at 12, so this branch answers for
       * suburbs as far out as Sunshine at 18km. Shopify's own zones decide how
       * far the van actually goes, and the theme cannot read them — so this says
       * a charge applies and lets checkout settle both questions.
       */
      say(
        "maybe",
        where + " is about " + km + "km from our " + suburb + " store, past our " +
          "free-delivery area, so an extra delivery charge may apply." +
          (borderline
            ? " Part of " + where + " is inside the free area, so it depends on " +
              "your street."
            : "") +
          " Pop your address in at checkout — it'll confirm we can reach you and " +
          "show the exact cost." + collectLine()
      );
    };

    pcForm.addEventListener("submit", (e) => {
      e.preventDefault();
      const value = (pcForm.querySelector("#postcode").value || "").trim();

      if (value === "") {
        say("bad", "Enter your suburb, for example Werribee.");
        return;
      }

      if (!bands.length || !measured.length || !storeLat || !storeLng) {
        // Misconfigured rather than out of area. Saying "we don't deliver there"
        // would be a false answer caused by our own settings.
        say(
          "bad",
          "We can't check that right now. Call us on the number below and we'll " +
            "tell you straight away."
        );
        return;
      }

      /*
       * A postcode still works, because people type them out of habit — but only
       * when every suburb sharing it lands in the same zone. Where they do not,
       * the honest answer is to ask which suburb, not to pick one. This is the
       * exact case the old postcode list got wrong.
       */
      if (/^\d{4}$/.test(value)) {
        const hits = measured.filter((s) => s.postcode === value);
        if (!hits.length) {
          say("no", tooFarFor(value));
          return;
        }
        // "Same answer" has to include sitting on a boundary, or a postcode
        // holding one borderline suburb and one comfortably inside would be
        // answered with the confident version of the wrong one.
        const zones = new Set(hits.map((s) => {
          if (nearBoundary(s.km)) return "edge";
          const z = zoneFor(s.km);
          return z ? z.km : "out";
        }));
        if (zones.size === 1) {
          answerFor(hits[0]);
          return;
        }
        const near = hits[0];
        const far = hits[hits.length - 1];
        say(
          "bad",
          value + " covers suburbs at quite different distances — " + pretty(near.name) +
            " is about " + round(near.km) + "km from us and " + pretty(far.name) +
            " about " + round(far.km) + "km. Type your suburb instead and we'll " +
            "give you the right answer."
        );
        return;
      }

      const needle = value.toUpperCase().replace(/\s+/g, " ");
      const exact = measured.find((s) => s.name === needle);
      if (exact) {
        answerFor(exact);
        return;
      }

      // One unambiguous prefix match is a typo or a half-typed name; several
      // means we would be guessing, so list them instead.
      const partial = measured.filter((s) => s.name.startsWith(needle));
      if (partial.length === 1) {
        answerFor(partial[0]);
        return;
      }
      if (partial.length > 1) {
        say(
          "bad",
          "Did you mean " + partial.slice(0, 4).map((s) => pretty(s.name)).join(", ") + "?"
        );
        return;
      }

      say("no", tooFarFor(value));
    });
  }

  /* ------------------------------------------------------------ filters -- */
  const filterToggle = document.querySelector("[data-filter-toggle]");
  const facets = document.querySelector("[data-facets]");
  const collLayout = document.querySelector(".coll-layout");

  // The rail starts hidden on every page at every width and Filter reveals it —
  // one meaning for the button, so its aria-expanded is never a lie about the
  // other breakpoint. Both classes are set on every toggle and CSS picks the one
  // that applies: a column above 860px, an overlay drawer below.
  const railIsWide = () => window.matchMedia("(min-width: 861px)").matches;

  if (filterToggle && facets) {
    const railOpen = () => facets.classList.contains("show");

    // Below 860px the rail is a fixed drawer, so it needs a backdrop and a
    // scroll lock like any other. Above it, it is just a column.
    const facetScrim = document.querySelector("[data-facets-scrim]");

    const setRail = (open) => {
      facets.classList.toggle("show", open);
      if (collLayout) collLayout.classList.toggle("filters-open", open);

      if (railIsWide()) {
        if (facetScrim) facetScrim.hidden = true;
        lockScroll("facets", false);
      } else {
        if (facetScrim) facetScrim.hidden = !open;
        lockScroll("facets", open);
        if (open) {
          const close = facets.querySelector("[data-facets-close]");
          if (close) close.focus();
        } else {
          filterToggle.focus();
        }
      }
      filterToggle.setAttribute("aria-expanded", String(open));
    };

    filterToggle.addEventListener("click", () => setRail(!railOpen()));
    if (facetScrim) facetScrim.addEventListener("click", () => setRail(false));
    facets.querySelector("[data-facets-close]")?.addEventListener("click", () => setRail(false));
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && !railIsWide() && facets.classList.contains("show")) setRail(false);
    });

    // Reopen the rail on load when filters are applied. It is closed by default
    // everywhere now, so after a filter submit the page came back with the grid
    // changed but every control hidden — indistinguishable from nothing having
    // happened, which is what it was reported as. The applied chips live inside
    // the rail, so this is also the only thing that shows what is switched on.
    if (Number(facets.getAttribute("data-active-filters") || 0) > 0) setRail(true);
    else filterToggle.setAttribute("aria-expanded", "false");

    // Crossing the breakpoint changes the rail from a column to an overlay, so
    // the scrim and the scroll lock have to be re-derived — resizing a desktop
    // window down with the rail open otherwise leaves an unbackdropped drawer,
    // and widening it back leaves the page scroll locked.
    window.matchMedia("(min-width: 861px)").addEventListener?.("change", () => {
      if (railOpen()) setRail(true);
    });
  }

  /* ------------------------------------------------------ view switcher -- */
  // Presentation only: no reload, no URL parameter, nothing the server needs to
  // know. Revealed here rather than in the markup so it never appears without
  // the script that makes it work.
  const viewas = document.querySelector("[data-viewas]");
  const productGrid = document.querySelector(".grid");
  if (viewas && productGrid) {
    const VIEW_KEY = "ktm:view";
    const applyView = (mode) => {
      productGrid.classList.toggle("is-list", mode === "list");
      viewas.querySelectorAll("[data-view]").forEach((b) =>
        b.setAttribute("aria-pressed", String(b.getAttribute("data-view") === mode)));
      try { localStorage.setItem(VIEW_KEY, mode); } catch { /* private mode */ }
    };

    let saved = "grid";
    try { saved = localStorage.getItem(VIEW_KEY) || "grid"; } catch { /* private mode */ }
    applyView(saved === "list" ? "list" : "grid");
    viewas.hidden = false;

    viewas.addEventListener("click", (e) => {
      const btn = e.target.closest("[data-view]");
      if (!btn) return;
      const mode = btn.getAttribute("data-view");
      applyView(mode);
      announce(mode === "list" ? "List view" : "Grid view");
    });
  }

  /* ------------------------------------------------------ facet extras --- */
  // "Show more" on long value lists (Brand can run to dozens of vendors).
  document.addEventListener("click", (e) => {
    const btn = e.target.closest("[data-facet-more-toggle]");
    if (!btn) return;
    const list = btn.parentElement.querySelector("[data-facet-more]");
    if (!list) return;
    const open = list.classList.toggle("show-all");
    btn.setAttribute("aria-expanded", String(open));
    btn.textContent = open ? "Show less" : "Show more";
  });

  // Dual-range price slider, wired to the number inputs it mirrors. Hidden in
  // the markup so it never shows without this.
  document.querySelectorAll("[data-price-slider]").forEach((slider) => {
    const lo = slider.querySelector("[data-ps-lo]");
    const hi = slider.querySelector("[data-ps-hi]");
    const fill = slider.querySelector("[data-ps-fill]");
    const body = slider.closest(".facet-body") || document;
    const minInput = body.querySelector("[data-price-min]");
    const maxInput = body.querySelector("[data-price-max]");
    if (!lo || !hi || !minInput || !maxInput) return;

    const max = Number(slider.getAttribute("data-range-max")) || 100;

    const paint = () => {
      // Keep the thumbs from crossing over each other.
      if (Number(lo.value) > Number(hi.value)) {
        if (document.activeElement === lo) lo.value = hi.value;
        else hi.value = lo.value;
      }
      const a = (Number(lo.value) / max) * 100;
      const b = (Number(hi.value) / max) * 100;
      if (fill) { fill.style.left = a + "%"; fill.style.right = 100 - b + "%"; }
    };

    const pushToInputs = () => {
      minInput.value = Number(lo.value) > 0 ? lo.value : "";
      maxInput.value = Number(hi.value) < max ? hi.value : "";
    };

    [lo, hi].forEach((r) => {
      r.addEventListener("input", () => { paint(); pushToInputs(); });
      // Submit on release, not on every pixel of the drag.
      r.addEventListener("change", () => {
        pushToInputs();
        minInput.dispatchEvent(new Event("change", { bubbles: true }));
      });
    });

    // Typing in a box moves the matching thumb.
    minInput.addEventListener("input", () => { lo.value = minInput.value || 0; paint(); });
    maxInput.addEventListener("input", () => { hi.value = maxInput.value || max; paint(); });

    paint();
    slider.hidden = false;
  });

  // Facets and sort submit the form; server-side filtering keeps the 450-product
  // grid off the client (spec §6.3).
  //
  // querySelectorAll, not querySelector: there are TWO of these forms — the facet
  // rail and the sort toolbar. Only the first in the DOM was ever wired, so the
  // sort dropdown had been silently dead on every collection and search page.
  document.querySelectorAll("[data-filter-form]").forEach((form) => {
    form.addEventListener("change", () => {
      // An untouched price box submits `filter.v.price.gte=` — harmless to
      // Shopify, but it litters the URL and makes a bare sort look filtered.
      form.querySelectorAll("input[type='number']").forEach((input) => {
        input.disabled = input.value.trim() === "";
      });
      form.submit();
    });
  });

  /* ---------------------------------------------------- quantity stepper -- */
  document.addEventListener("click", (e) => {
    const btn = e.target.closest("[data-qty-step]");
    if (!btn) return;
    const input = document.querySelector("[data-qty-input]");
    if (!input) return;
    const step = parseInt(btn.getAttribute("data-qty-step"), 10);
    input.value = String(Math.max(1, (parseInt(input.value, 10) || 1) + step));
  });

  /* ------------------------------------------------------ newsletter/etc -- */
  document.addEventListener("submit", (e) => {
    const form = e.target.closest("[data-newsletter]");
    if (!form) return;
    e.preventDefault();
    form.reset();
    toast("You're subscribed! \u{1F389}");
    announce("Subscribed to the newsletter.");
  });

  document.addEventListener("click", (e) => {
    const btn = e.target.closest("[data-copy-code]");
    if (!btn) return;
    const code = btn.getAttribute("data-copy-code");
    if (navigator.clipboard) navigator.clipboard.writeText(code);
    toast("Code " + code + " copied \u{1F39F}️");
  });

  /* ------------------------------------------------------------ carousels -- */
  /*
   * Arrows are progressive enhancement — the track is a native scroller, so it
   * still works with JS off, on touch, and with a keyboard (it is focusable).
   */
  document.querySelectorAll("[data-carousel]").forEach((car) => {
    const track = car.querySelector("[data-car-track]");
    const prev = car.querySelector("[data-car-prev]");
    const next = car.querySelector("[data-car-next]");
    if (!track || !prev || !next) return;

    const update = () => {
      const max = track.scrollWidth - track.clientWidth;
      const scrollable = max > 4;
      prev.hidden = !scrollable || track.scrollLeft <= 4;
      next.hidden = !scrollable || track.scrollLeft >= max - 4;
    };

    const page = (dir) => {
      const card = track.firstElementChild;
      const step = card ? card.getBoundingClientRect().width + 18 : track.clientWidth * 0.8;
      track.scrollBy({ left: dir * step * 2, behavior: "smooth" });
    };

    prev.addEventListener("click", () => page(-1));
    next.addEventListener("click", () => page(1));
    track.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);

    // Images load late and change scrollWidth, so re-check once settled.
    update();
    setTimeout(update, 400);
    window.addEventListener("load", update);
  });

  /* --------------------------------------------------- predictive search -- */
  /**
   * Debounced predictive search (§6.5). Results are built with DOM construction.
   *
   * Two response shapes, because the dev harness and Shopify do not agree and the
   * comment that used to sit here ("the endpoint mirrors Shopify's shape, so on
   * integration only the URL changes") was simply wrong. Shopify returns
   *
   *   { resources: { results: { products: [...], collections: [...] } } }
   *
   * with prices as dollar STRINGS ("6.20"), images at featured_image.url, and no
   * product count on collections. The harness returns a flat { products,
   * collections, query } with prices in cents and images as plain strings.
   *
   * Reading the flat shape only meant `data.products` was undefined against the
   * real store, which threw inside render(), hit the catch, and closed the panel
   * — predictive search was dead on Shopify while ten local tests passed.
   * normalise() is what keeps both honest.
   */
  function normalise(data, query) {
    const nested = data && data.resources && data.resources.results;
    const src = nested || data || {};
    const list = (v) => (Array.isArray(v) ? v : []);
    return {
      query: (data && data.query) || query,
      products: list(src.products).map((p) => ({
        title: p.title,
        url: p.url,
        available: p.available !== false,
        // Cents locally, a dollar string on Shopify. Normalise to cents.
        price: typeof p.price === "string" ? Math.round(parseFloat(p.price) * 100) : p.price,
        image: (p.featured_image && p.featured_image.url) || p.image || null,
      })),
      collections: list(src.collections).map((c) => ({
        title: c.title,
        url: c.url,
        // Shopify's suggest payload carries no product count; omit rather than
        // render "(undefined)".
        count: typeof c.count === "number" ? c.count : null,
      })),
    };
  }
  const searchForm = document.querySelector("[data-search-form]");
  const searchInput = document.querySelector("[data-search-input]");
  const results = document.querySelector("[data-search-results]");

  if (searchForm && searchInput && results) {
    let timer;
    let controller;
    let activeIndex = -1;

    const close = () => {
      results.hidden = true;
      results.replaceChildren();
      searchInput.setAttribute("aria-expanded", "false");
      activeIndex = -1;
    };

    function row(item) {
      const a = document.createElement("a");
      a.className = "ps-item";
      a.href = item.url;
      a.setAttribute("role", "option");

      if (item.image) {
        const img = document.createElement("img");
        img.src = item.image;
        img.alt = "";
        img.width = 40;
        img.height = 40;
        img.loading = "lazy";
        a.appendChild(img);
      }
      const box = document.createElement("span");
      box.className = "ps-info";

      const t = document.createElement("span");
      t.className = "ps-title";
      t.textContent = item.title; // untrusted catalogue data
      box.appendChild(t);

      const meta = document.createElement("span");
      meta.className = "ps-meta";
      meta.textContent = money(item.price) + (item.available ? "" : " · Sold out");
      box.appendChild(meta);

      a.appendChild(box);
      return a;
    }

    function render(data) {
      results.replaceChildren();
      const hasAny = data.products.length || data.collections.length;

      if (!hasAny) {
        const p = document.createElement("p");
        p.className = "ps-empty";
        p.textContent = 'No matches for "' + data.query + '".';
        results.appendChild(p);
        const link = document.createElement("a");
        link.className = "ps-all";
        link.href = "/search?q=" + encodeURIComponent(data.query);
        link.textContent = "Search all products →";
        results.appendChild(link);
      } else {
        if (data.collections.length) {
          const h = document.createElement("h4");
          h.textContent = "Departments";
          results.appendChild(h);
          data.collections.forEach((c) => {
            const a = document.createElement("a");
            a.className = "ps-item ps-dept";
            a.href = c.url;
            a.setAttribute("role", "option");
            a.textContent = c.count === null ? c.title : c.title + " (" + c.count + ")";
            results.appendChild(a);
          });
        }
        if (data.products.length) {
          const h = document.createElement("h4");
          h.textContent = "Products";
          results.appendChild(h);
          data.products.forEach((p) => results.appendChild(row(p)));
        }
        const all = document.createElement("a");
        all.className = "ps-all";
        all.href = "/search?q=" + encodeURIComponent(data.query);
        all.textContent = "See all results →";
        results.appendChild(all);
      }

      results.hidden = false;
      searchInput.setAttribute("aria-expanded", "true");
      activeIndex = -1;
    }

    searchInput.addEventListener("input", () => {
      const q = searchInput.value.trim();
      clearTimeout(timer);
      if (q.length < 2) return close();
      timer = setTimeout(() => {
        if (controller) controller.abort();
        controller = new AbortController();
        // resources[type]/resources[limit] are what Shopify's API actually reads;
        // a bare `limit` is ignored there. The harness ignores them in turn.
        fetch("/search/suggest.json?q=" + encodeURIComponent(q) +
              "&limit=6&resources[type]=product,collection&resources[limit]=6", {
          signal: controller.signal,
        })
          .then((r) => (r.ok ? r.json() : Promise.reject(new Error(r.status))))
          .then((data) => render(normalise(data, q)))
          .catch((err) => {
            if (err.name === "AbortError") return; // superseded by a newer keystroke
            // Don't fail silently — a broken suggest endpoint should be visible.
            console.error("[ktm] predictive search failed:", err);
            close();
          });
      }, 200);
    });

    // Keyboard: arrows move through options, Escape closes.
    searchInput.addEventListener("keydown", (e) => {
      const opts = Array.from(results.querySelectorAll('[role="option"]'));
      if (e.key === "Escape") return close();
      if (!opts.length) return;
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        activeIndex += e.key === "ArrowDown" ? 1 : -1;
        if (activeIndex < 0) activeIndex = opts.length - 1;
        if (activeIndex >= opts.length) activeIndex = 0;
        opts.forEach((o, i) => o.classList.toggle("active", i === activeIndex));
        opts[activeIndex].focus();
      }
      if (e.key === "Enter" && activeIndex >= 0) {
        e.preventDefault();
        opts[activeIndex].click();
      }
    });

    document.addEventListener("click", (e) => {
      if (!e.target.closest("[data-search-form]")) close();
    });
  }

  refresh().catch(() => renderCart());
})();
